import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axiosInstance from "../api/axiosInstance";
import { useAlert } from "./AlertContext";
import { useAuth } from "./AuthContext";
import { trackEvent } from "../hooks/useAnalytics";
import { mergeLoadedMessages } from "../utils/mergeLoadedMessages";

const ChatContext = createContext();

const PAGE_SIZE = 30;
const TYPING_TTL = 2500;
const TYPING_THROTTLE = 1200;
const SEARCH_DEBOUNCE = 300;
// Základ dočasných ID optimistických zpráv – musí být větší než ID ze serveru
const TEMP_ID_BASE = 9000000000;
// Echo si prefix "presence-" přidává samo; vzor v routes/channels.php je "app"
const PRESENCE_CHANNEL = "app";

// Člen presence může přijít jako {id, info} (here) nebo jako samotné user_info (joining/leaving)
const presenceMemberId = (member) => {
  const raw = member?.id ?? member?.user_id ?? member?.info?.id;

  return raw === undefined || raw === null ? null : Number(raw);
};

export const ChatProvider = ({ children }) => {
  const { user, accessToken } = useAuth();
  const { showAlert } = useAlert();
  const userId = user?.id;

  const [conversations, setConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [messagesByConversation, setMessagesByConversation] = useState({});
  const [metaByConversation, setMetaByConversation] = useState({});
  const [typingByConversation, setTypingByConversation] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null);
  // Cíl scrollu po otevření konverzace z výsledků vyhledávání
  const [searchTarget, setSearchTarget] = useState(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState([]);

  const channelRef = useRef(null);
  const lastTypingSentAt = useRef(0);
  const searchTimer = useRef(null);
  const typingTimers = useRef({});
  const pendingContextRef = useRef(null);
  // Čítač dočasných ID optimistických zpráv
  const tempMessageId = useRef(0);
  // Reflection of pagination/loading state so repeated scroll events don't
  // queue the same page multiple times
  const metaRef = useRef({});
  // Kolik stránek zpráv máme v seznamu pro každou konverzaci načteno
  const loadedPagesRef = useRef({});
  const isLoadingMessagesRef = useRef(false);

  // Aktualizuje konverzaci v seznamu i v aktuálních výsledcích vyhledávání
  const upsertConversation = useCallback((summary) => {
    if (!summary) return;

    const merge = (prev) => {
      if (!prev) return prev;

      return [summary, ...prev.filter((c) => c.id !== summary.id)];
    };

    setConversations((prev) => {
      const rest = prev.filter((c) => c.id !== summary.id);
      return [summary, ...rest];
    });
    setSearchResults(merge);
  }, []);

  const removeConversation = useCallback((conversationId) => {
    setConversations((prev) => prev.filter((c) => c.id !== conversationId));
    setSearchResults(
      (prev) => prev?.filter((c) => c.id !== conversationId) ?? null,
    );
    setActiveConversationId((current) =>
      current === conversationId ? null : current,
    );
  }, []);

  // Full-text vyhledávání konverzací (jména účastníků, text všech zpráv, kontext)
  const searchConversations = useCallback((term) => {
    setSearchTerm(term);
    clearTimeout(searchTimer.current);

    const query = term.trim();

    if (!query) {
      setSearchResults(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.get(
          `/api/conversations?q=${encodeURIComponent(query)}`,
        );
        setSearchResults(res.data.conversations || []);
      } catch (err) {
        console.error("Chyba vyhledávání konverzací:", err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE);
  }, []);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const upsertMessage = useCallback((conversationId, message) => {
    setMessagesByConversation((prev) => {
      const list = prev[conversationId] || [];
      const index = list.findIndex((m) => m.id === message.id);

      if (index === -1) {
        return {
          ...prev,
          [conversationId]: [...list, message].sort((a, b) => a.id - b.id),
        };
      }

      const next = [...list];
      next[index] = { ...next[index], ...message };

      return { ...prev, [conversationId]: next };
    });
  }, []);

  // Nahradí dočasnou (optimistickou) zprávu serverovým záznamem.
  // Serverová zpráva dorazí i přes echo broadcast, takže se může objevit
  // dřív než odpověď – v tom případě se jen doplní a dočasná se odstraní.
  const replaceMessage = useCallback((conversationId, tempId, message) => {
    setMessagesByConversation((prev) => {
      const list = prev[conversationId] || [];
      const withoutTemp = list.filter((m) => m.id !== tempId);

      if (withoutTemp.some((m) => m.id === message.id)) {
        return {
          ...prev,
          [conversationId]: withoutTemp.map((m) =>
            m.id === message.id ? { ...m, ...message } : m,
          ),
        };
      }

      return {
        ...prev,
        [conversationId]: [...withoutTemp, message].sort((a, b) => a.id - b.id),
      };
    });
  }, []);

  // Aktualizace náhledu v seznamu konverzací po odeslání/úpravě/smazání vlastní zprávy
  const patchConversationPreview = useCallback((conversationId, message) => {
    setConversations((prev) =>
      prev.map((conversation) =>
        conversation.id === conversationId
          ? {
              ...conversation,
              last_message: message || conversation.last_message,
              unread_count: message ? 0 : conversation.unread_count,
              updated_at: message
                ? message.created_at
                : conversation.updated_at,
            }
          : conversation,
      ),
    );
  }, []);

  const removeMessage = useCallback((conversationId, messageId) => {
    setMessagesByConversation((prev) => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).filter(
        (m) => m.id !== messageId,
      ),
    }));
  }, []);

  // Protistrana zprávu dostala do seznamu – dvě šedé fajfky místo jedné
  const markLocalDelivered = useCallback(
    (conversationId, messageIds, deliveredAt) => {
      if (!messageIds?.length) return;

      const ids = new Set(messageIds);

      setMessagesByConversation((prev) => {
        const list = prev[conversationId];
        if (!list) return prev;

        return {
          ...prev,
          [conversationId]: list.map((m) =>
            ids.has(m.id) && !m.delivered_at
              ? { ...m, delivered_at: deliveredAt }
              : m,
          ),
        };
      });
    },
    [],
  );

  const markLocalRead = useCallback((conversationId, readAt) => {
    setMessagesByConversation((prev) => {
      const list = prev[conversationId];
      if (!list) return prev;

      return {
        ...prev,
        [conversationId]: list.map((m) =>
          m.read_at ? m : { ...m, read_at: readAt },
        ),
      };
    });
  }, []);

  const markConversationReadLocally = useCallback((conversationId) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId ? { ...c, unread_count: 0 } : c,
      ),
    );
  }, []);

  // Seznam konverzací + realtime změny stavu konverzací (badge, předčítaná hláška)
  useEffect(() => {
    if (!userId || !accessToken) return undefined;

    let cancelled = false;

    const loadConversations = async () => {
      try {
        const res = await axiosInstance.get("/api/conversations");
        if (!cancelled) setConversations(res.data.conversations || []);
      } catch (err) {
        console.error("Chyba načtení konverzací:", err);
      }
    };

    loadConversations();

    const channel = window.Echo?.private(`user.${userId}`);

    channel?.listen(".chat.updated", ({ conversation }) => {
      if (!cancelled) upsertConversation(conversation);
    });

    channel?.listen(".chat.removed", ({ conversation_id }) => {
      if (!cancelled) removeConversation(conversation_id);
    });

    return () => {
      cancelled = true;
      // Kanál sdílí i NotificationContext, proto se zde ruší jen naše listenery
      channel?.stopListening(".chat.updated");
      channel?.stopListening(".chat.removed");
    };
  }, [userId, accessToken, upsertConversation, removeConversation]);

  const loadMessages = useCallback(
    async (conversationId, page = 1) => {
      if (!conversationId) return;

      isLoadingMessagesRef.current = true;
      setIsLoadingMessages(true);

      try {
        const res = await axiosInstance.get(
          `/api/conversations/${conversationId}/messages?per_page=${PAGE_SIZE}&page=${page}`,
        );

        const messages = res.data.messages || [];

        setMessagesByConversation((prev) => ({
          ...prev,
          [conversationId]: mergeLoadedMessages(
            prev[conversationId],
            messages,
            page,
          ),
        }));

        // Kolik stránek máme v seznamu skutečně načteno. Znovunačtení
        // první stránky z serveru vrací current_page = 1, ale starší
        // stránky v seznamu pořád máme – kdybychom to ignorovali,
        // klient by si je vyžádal znovu a zprávy by se objevily dvakrát.
        loadedPagesRef.current[conversationId] = Math.max(
          loadedPagesRef.current[conversationId] || 0,
          page,
        );

        const meta = {
          ...res.data.meta,
          current_page: loadedPagesRef.current[conversationId],
        };

        metaRef.current = { ...metaRef.current, [conversationId]: meta };
        setMetaByConversation((prev) => ({ ...prev, [conversationId]: meta }));
        upsertConversation(res.data.conversation);
      } catch (err) {
        console.error("Chyba načtení zpráv:", err);
      } finally {
        isLoadingMessagesRef.current = false;
        setIsLoadingMessages(false);
      }
    },
    [upsertConversation],
  );

  // Načtení další stránky starších zpráv. Stav se čte z ref, aby se dal
  // požadovat opakovaně i během jednoho scrollu.
  const loadOlderMessages = useCallback(
    (conversationId) => {
      const meta = metaRef.current[conversationId];

      // Vracíme, jestli se stránka opravdu načítá – chat si podle toho
      // nastaví zálohu pozice scrollu. Bez toho by si ji nastavil i
      // tehdy, když už se nic načítat nemusí, a zůstala by viset.
      if (
        !meta ||
        meta.current_page >= meta.last_page ||
        isLoadingMessagesRef.current
      ) {
        return false;
      }

      isLoadingMessagesRef.current = true;
      loadMessages(conversationId, meta.current_page + 1).finally(() => {
        isLoadingMessagesRef.current = false;
      });

      return true;
    },
    [loadMessages],
  );

  const markAsRead = useCallback(
    async (conversationId) => {
      if (!conversationId) return;

      markConversationReadLocally(conversationId);

      try {
        const res = await axiosInstance.post(
          `/api/conversations/${conversationId}/read`,
        );

        if (res.data.count > 0) {
          markLocalRead(conversationId, res.data.read_at);
        }
      } catch (err) {
        console.error("Chyba označení zpráv jako přečtených:", err);
      }
    },
    [markConversationReadLocally, markLocalRead],
  );

  // Přesný timeout 2,5 s od posledního whisperu – stav se sám vypne
  const markTyping = useCallback((conversationId, typingUserId) => {
    const key = `${conversationId}:${typingUserId}`;

    if (typingTimers.current[key]) {
      clearTimeout(typingTimers.current[key]);
    }

    setTypingByConversation((prev) => ({
      ...prev,
      [conversationId]: {
        ...(prev[conversationId] || {}),
        [typingUserId]: Date.now(),
      },
    }));

    typingTimers.current[key] = setTimeout(() => {
      delete typingTimers.current[key];

      setTypingByConversation((prev) => {
        const users = { ...(prev[conversationId] || {}) };
        delete users[typingUserId];

        if (!Object.keys(users).length) {
          const next = { ...prev };
          delete next[conversationId];

          return next;
        }

        return { ...prev, [conversationId]: users };
      });
    }, TYPING_TTL);
  }, []);

  const clearTyping = useCallback((conversationId) => {
    Object.keys(typingTimers.current).forEach((key) => {
      if (key.startsWith(`${conversationId}:`)) {
        clearTimeout(typingTimers.current[key]);
        delete typingTimers.current[key];
      }
    });

    setTypingByConversation((prev) => {
      if (!prev[conversationId]) return prev;

      const next = { ...prev };
      delete next[conversationId];

      return next;
    });
  }, []);

  useEffect(
    () => () => {
      Object.values(typingTimers.current).forEach((timer) =>
        clearTimeout(timer),
      );
      typingTimers.current = {};
    },
    [],
  );

  // Presence (online/offline) přes kanál "presence-app".
  // Čeká se na access token, jinak by autorizace proběhla bez přihlášení.
  useEffect(() => {
    if (!accessToken) return undefined;

    let channel = window.Echo?.join(PRESENCE_CHANNEL);

    if (!channel) return undefined;

    const syncHere = (members) => {
      try {
        const ids = (members || []).map(presenceMemberId).filter((id) => id !== null);
        setOnlineUserIds(ids);
      } catch (e) {
        console.error("Error syncing presence here:", e);
      }
    };

    channel.here(syncHere);

    channel.joining((member) => {
      try {
        const id = presenceMemberId(member);
        if (id === null) return;
        setOnlineUserIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      } catch (e) {
        console.error("Error in presence joining:", e);
      }
    });

    channel.leaving((member) => {
      try {
        const id = presenceMemberId(member);
        if (id === null) return;
        setOnlineUserIds((prev) => prev.filter((existing) => existing !== id));
      } catch (e) {
        console.error("Error in presence leaving:", e);
      }
    });

    return () => {
      // NotificationContext sdílí Echo instanci, proto kanál pouze odposloucháváme
      try {
        if (channel && typeof channel.stopListening === 'function') {
          channel.stopListening();
        }
      } catch (e) {
        console.error("Error stopping presence channel:", e);
      }
      setOnlineUserIds([]);
    };
  }, [accessToken]);

  const isOnline = useCallback(
    (targetUserId) => {
      if (!targetUserId) return false;

      return onlineUserIds.includes(Number(targetUserId));
    },
    [onlineUserIds],
  );

  // Typující uživatelé v aktivní konverzaci (s vyloučením sebe)
  const typingUserIds = useMemo(() => {
    const users = activeConversationId
      ? typingByConversation[activeConversationId] || {}
      : {};

    return Object.keys(users)
      .map(Number)
      .filter((id) => id !== Number(userId));
  }, [activeConversationId, typingByConversation, userId]);

  // Realtime zprávy v otevřené konverzaci
  useEffect(() => {
    if (!activeConversationId) {
      if (channelRef.current) {
        window.Echo?.leave(channelRef.current.name);
        channelRef.current = null;
      }
      return undefined;
    }

    const conversationId = activeConversationId;
    // Echo si prefix "private-" přidává samo – vzor v routes/channels.php je "chat.{conversationId}"
    const channelName = `chat.${conversationId}`;
    const channel = window.Echo?.private(channelName);

    if (!channel) return undefined;

    channelRef.current = { channel, name: channelName };

    channel.listen(".message.sent", ({ message }) => {
      if (message.conversation_id !== conversationId) return;

      upsertMessage(conversationId, message);
      markAsRead(conversationId);
    });

    channel.listen(".message.updated", ({ message }) => {
      if (message.conversation_id !== conversationId) return;

      upsertMessage(conversationId, message);
    });

    channel.listen(
      ".message.deleted",
      ({ message_id, conversation_deleted, context_message_id, message }) => {
        // Protistrana vidí zprávu jako „Zpráva smazána“
        if (message?.id) {
          upsertMessage(conversationId, message);
        } else {
          removeMessage(conversationId, message_id);
        }

        // Hláška „📌 Reakce na …“ patřila smazané zprávě – zmizí s ní,
        // jinak by zůstala viset bez kontextu.
        if (context_message_id) {
          removeMessage(conversationId, context_message_id);
        }

        if (conversation_deleted) {
          removeConversation(conversationId);
        }
      },
    );

    channel.listen(
      ".message.delivered",
      ({ conversation_id, message_ids, delivered_at }) => {
        if (conversation_id !== conversationId) return;

        markLocalDelivered(conversationId, message_ids, delivered_at);
      },
    );

    channel.listen(".message.read", ({ conversation_id, read_at }) => {
      if (conversation_id !== conversationId) return;

      markLocalRead(conversation_id, read_at);
    });

    // Whisper (client event) "protistrana píše" – stav se obnovuje každých 2,5 s
    channel.listenForWhisper("typing", (payload) => {
      const typingUserId = payload?.user_id;

      if (!typingUserId || typingUserId === userId) return;

      markTyping(conversationId, typingUserId);
    });

    // Záloha přes serverový event, když nejsou dostupné client events
    channel.listen(".message.typing", ({ conversation_id, user_id }) => {
      if (conversation_id !== conversationId || !user_id || user_id === userId)
        return;

      markTyping(conversationId, user_id);
    });

    loadMessages(conversationId, 1);
    markAsRead(conversationId);

    return () => {
      window.Echo?.leave(channelName);
      channelRef.current = null;
      clearTyping(conversationId);
    };
  }, [
    activeConversationId,
    userId,
    upsertMessage,
    removeMessage,
    removeConversation,
    markLocalRead,
    markLocalDelivered,
    markAsRead,
    loadMessages,
    markTyping,
    clearTyping,
  ]);

  const openChat = useCallback((conversationId, target = null) => {
    setActiveConversationId(conversationId);
    setSearchTarget(target);
    trackEvent("conversation_open", { conversation_id: conversationId });
  }, []);

  const clearSearchTarget = useCallback(() => setSearchTarget(null), []);

  const closeChat = useCallback(() => {
    setActiveConversationId(null);
    setSearchTarget(null);
  }, []);

  // Najde nebo založí konverzaci s protistranou a otevře ji.
  // Reakce na inzerát/profil se uloží a připojí k první odeslané zprávě.
  const openChatWith = useCallback(
    async (recipientId, context = null) => {
      try {
        const res = await axiosInstance.post("/api/conversations", {
          recipient_id: recipientId,
          ...(context || {}),
        });

        upsertConversation(res.data.conversation);
        setActiveConversationId(res.data.conversation.id);
        setSearchTerm("");
        setSearchResults(null);
        pendingContextRef.current = context || null;

        trackEvent("conversation_create", {
          conversation_id: res.data.conversation.id,
          recipient_id: recipientId,
          context_type: context?.type ?? null,
        });

        return res.data.conversation;
      } catch (err) {
        console.error("Chyba založení konverzace:", err);
        showAlert("error", "Konverzaci se nepodařilo otevřít.");
        return null;
      }
    },
    [showAlert, upsertConversation],
  );

  const sendMessage = useCallback(
    async (body, files = []) => {
      if (!activeConversationId) return;

      const trimmedBody = (body || "").trim();

      // Zpráva musí mít text, nebo alespoň jednu přílohu
      if (!trimmedBody && files.length === 0) return;

      setIsSending(true);

      // Reakce se přiloží jen k první zprávě, kterou uživatel po otevření chatu odešle
      const context = pendingContextRef.current;
      pendingContextRef.current = null;

      const formData = new FormData();

      formData.append("body", trimmedBody);

      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });
      }

      files.forEach(({ file }) => formData.append("attachments[]", file));

      // Zpráva se zobrazí hned, ještě než se nahrají fotky.
      // Dočasné ID je číselné a větší než cokoliv ze serveru, aby se
      // v seznamu (řazeném podle ID) drželo na konci.
      tempMessageId.current += 1;

      // Fotky a text se ukládají jako dvě zprávy – stejně i v náhledu,
      // ať se obě mažou nezávisle
      const hasFiles = files.length > 0;
      const split = hasFiles && trimmedBody !== "";

      const tempId = TEMP_ID_BASE + tempMessageId.current;
      const photoTempId = split ? tempId + 1 : tempId;

      const buildOptimistic = (id, messageBody, attachments) => ({
        id,
        conversation_id: activeConversationId,
        user_id: userId,
        sender: user,
        body: messageBody,
        attachments,
        is_system: false,
        is_pending: true,
        is_edited: false,
        is_deleted: false,
        read_at: null,
        created_at: new Date().toISOString(),
      });

      const photoAttachments = files.map(({ file, url }) => ({
        url,
        name: file.name,
      }));

      const optimisticMessage = split
        ? buildOptimistic(photoTempId, "", photoAttachments)
        : buildOptimistic(tempId, trimmedBody, photoAttachments);

      upsertMessage(activeConversationId, optimisticMessage);

      if (split) {
        upsertMessage(
          activeConversationId,
          buildOptimistic(tempId, trimmedBody, []),
        );
      }

      patchConversationPreview(activeConversationId, optimisticMessage);

      try {
        const res = await axiosInstance.post(
          `/api/conversations/${activeConversationId}/messages`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        const serverMessages = res.data.messages || [res.data.message];
        const textMessage = split ? serverMessages[1] : serverMessages[0];

        // Server vrací fotky a text jako dvě zprávy
        serverMessages.forEach((message, index) => {
          const tempKey = split ? (index === 0 ? photoTempId : tempId) : tempId;

          replaceMessage(activeConversationId, tempKey, message);
        });

        patchConversationPreview(
          activeConversationId,
          textMessage || serverMessages[0],
        );

        if (res.data.context_message) {
          upsertMessage(activeConversationId, res.data.context_message);
        }

        // Náhledy dočasné zprávy už nejsou potřeba
        files.forEach(({ url }) => URL.revokeObjectURL(url));
      } catch (err) {
        removeMessage(activeConversationId, split ? photoTempId : tempId);

        if (split) removeMessage(activeConversationId, tempId);

        if (context) {
          pendingContextRef.current = context;
        }

        console.error("Chyba odeslání zprávy:", err);
        showAlert(
          "error",
          err.response?.data?.message || "Zprávu se nepodařilo odeslat.",
        );
      } finally {
        setIsSending(false);
      }
    },
    [
      activeConversationId,
      user,
      userId,
      showAlert,
      upsertMessage,
      replaceMessage,
      removeMessage,
      patchConversationPreview,
    ],
  );

  const deleteMessage = useCallback(
    async (messageId) => {
      if (!activeConversationId) return;

      try {
        const res = await axiosInstance.delete(`/api/messages/${messageId}`);

        // Zpráva nezmizí, jen se označí jako smazaná
        if (res.data.message) {
          upsertMessage(activeConversationId, res.data.message);
        }

        // Reakce navázaná na zprávu se smaže spolu s ní
        if (res.data.context_message_id) {
          removeMessage(activeConversationId, res.data.context_message_id);
        }

        patchConversationPreview(activeConversationId, null);

        if (res.data.conversation_deleted) {
          setConversations((prev) =>
            prev.filter((c) => c.id !== activeConversationId),
          );
          closeChat();
        }
      } catch (err) {
        console.error("Chyba smazání zprávy:", err);
        showAlert(
          "error",
          err.response?.data?.message || "Zprávu se nepodařilo smazat.",
        );
      }
    },
    [
      activeConversationId,
      upsertMessage,
      removeMessage,
      closeChat,
      showAlert,
      patchConversationPreview,
    ],
  );

  // Odeslání indikátoru psaní (throttled, aby se neposílala na každý stisk)
  const notifyTyping = useCallback(() => {
    if (!activeConversationId) return;

    const now = Date.now();

    if (now - lastTypingSentAt.current < TYPING_THROTTLE) return;

    lastTypingSentAt.current = now;

    // Whisper funguje jen po úspěšném přihlášení ke kanálu
    const subscription = channelRef.current?.channel?.subscription;

    if (subscription?.subscribed) {
      channelRef.current.channel.whisper("typing", { user_id: userId });
    }

    // Záloha přes API pro klienty bez podpory client events
    axiosInstance
      .post(`/api/conversations/${activeConversationId}/typing`)
      .catch(() => {});
  }, [activeConversationId, userId]);

  const markAllAsRead = useCallback(() => {
    conversations.forEach((conversation) => {
      if (conversation.unread_count > 0) markAsRead(conversation.id);
    });
  }, [conversations, markAsRead]);

  const activeConversation = useMemo(
    () =>
      conversations.find((c) => c.id === activeConversationId) ||
      searchResults?.find((c) => c.id === activeConversationId) ||
      null,
    [conversations, searchResults, activeConversationId],
  );

  const unreadCount = useMemo(
    () => conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0),
    [conversations],
  );

  return (
    <ChatContext.Provider
      value={{
        conversations,
        searchTerm,
        searchResults,
        isSearching,
        searchConversations,
        unreadCount,
        activeConversation,
        activeConversationId,
        searchTarget,
        clearSearchTarget,
        messages: activeConversationId
          ? messagesByConversation[activeConversationId] || []
          : [],
        // Všechny načtené zprávy – používá se pro galerii fotek v chatu
        messagesByConversation,
        messagesMeta: activeConversationId
          ? metaByConversation[activeConversationId] || null
          : null,
        isTyping: typingUserIds.length > 0,
        typingUserIds,
        onlineUserIds,
        isOnline,
        isLoadingMessages,
        isSending,
        openChat,
        closeChat,
        openChatWith,
        sendMessage,
        deleteMessage,
        markAsRead,
        markAllAsRead,
        loadOlderMessages,
        notifyTyping,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);
